/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback } from "react";

import { emitEvent } from "@components/utils/analytics";

import { useCookie } from "./cookie.hooks";

import { useIsMac, useIsMobile } from ".";

/**
 * Handles downloading the launcher installer for the correct platform; doesn't download on mobile
 */
export const useDownloadLauncher = (windowsUrl: string, macUrl: string): (() => void) => {
	const isMac = useIsMac();
	const isMobile = useIsMobile();
	const epicSid = useCookie("_epicSID");

	const initiateDownload = useCallback(() => {
		if (typeof window !== "undefined" && !isMobile) {
			const link = document.createElement("a");
			const url = new URL(isMac ? macUrl : windowsUrl);
			if (epicSid) url.searchParams.append("trackingId", epicSid);
			const urlString = url.toString();
			link.href = urlString;
			link.setAttribute("download", "");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			emitEvent({
				eventCategory: "trigger",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: "trigger-launcher-download",
				eventValue: {
					component: "DownloadLauncher",
					urlString,
				},
			});
		}
	}, [epicSid, isMac, isMobile, macUrl, windowsUrl]);

	return initiateDownload;
};

export const useAutoStartDownload = (downloadUrl: string, component?: string): (() => void) => {
	const isMobile = useIsMobile();
	const epicSid = useCookie("_epicSID");

	const initiateDownload = useCallback(() => {
		if (!downloadUrl) {
			return;
		}

		if (typeof window !== "undefined" && !isMobile) {
			const link = document.createElement("a");
			const url = new URL(downloadUrl);
			if (epicSid) url.searchParams.append("trackingId", epicSid);
			const urlString = url.toString();
			link.href = urlString;
			link.setAttribute("download", "");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			emitEvent({
				eventCategory: "trigger",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: "cosmos-download",
				eventValue: {
					component: component || "",
					urlString,
				},
			});
		}
	}, [epicSid, isMobile, downloadUrl, component]);

	return initiateDownload;
};
